import { useNavigate } from 'react-router-dom';
import { ContentPromotionBudget } from '../../../ui/business/ContentPromotionBudget/ContentPromotionBudget';
import { ContentPromotionDate } from '../../../ui/business/ContentPromotionDate/ContentPromotionDate';
import { ContentPromotionLogin } from '../../../ui/business/ContentPromotionLogin/ContentPromotionLogin';
import { ContentPromotionPosts } from '../../../ui/business/ContentPromotionPosts/ContentPromotionPosts';
import { ContentPromotionStats } from '../../../ui/business/ContentPromotionStats/ContentPromotionStats';
import { ContentPromotionUtm } from '../../../ui/business/ContentPromotionUtm/ContentPromotionUtm';
import { GeoSuggestField } from '../../../ui/business/GeoSuggestField/GeoSuggestField';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { Button } from '../../../ui/input/Button';
import { Flex } from '../../../ui/layout/Flex';
import { Typography } from '../../../ui/typography/Typography';
import { useIsMobile } from '../../../utils/getIsMobile';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useIsContentPromotionEnabled } from '../../../utils/useIsContentPromotionEnabled';
import { useTranslation } from '../../../utils/useTranslation';
import { withBusinessAuthProtection } from '../../withBusinessAuthProtection';
import { useBusinessGeo } from '../hooks/useBusinessGeo';
import { DRAFT_MAX_WIDTH, STATS_MAX_WIDTH, TITLE } from './constants';
import { useCheckDirectAccessToken } from './hooks/useCheckDirectAccessToken';
import { useDirectLogins } from './hooks/useDirectLogins';
import { usePromotionDate } from './hooks/usePromotionDate';
import { usePromotionMetrics } from './hooks/usePromotionMetrics';
import { usePromotionPosts } from './hooks/usePromotionPosts';
import { usePromotionStrategy } from './hooks/usePromotionStrategy';
import { usePromotionUtm } from './hooks/usePromotionUtm';
import { useSavePromotion } from './hooks/useSavePromotion';
import { ContentPromotionProps } from './types';
import { isCampaignDisabled } from './utils';

export const ContentPromotion = withBusinessAuthProtection(
    ({ company, isEditMode }: ContentPromotionProps) => {
        const t = useTranslation();
        const navigate = useNavigate();
        const isMobile = useIsMobile();
        const contentPromotionEnabled = useIsContentPromotionEnabled();
        const enableWeeklyLimit = Boolean(useExperimentFlag('enable_promotion_weekly_limit'));
        const enableCrr = Boolean(useExperimentFlag('enable_promotion_crr'));

        useCheckDirectAccessToken();

        const { selectedLogin, isLoagingLogins, logins, onChangeLogin } = useDirectLogins(
            company,
            isEditMode,
        );

        const {
            posts,
            postsValue,
            disabledPosts,
            visiblePostsDialog,
            onPostsDialogClose,
            onPostsDialogSave,
            onPostsFieldChange,
        } = usePromotionPosts(company);

        const {
            metrics,
            selectedCounters,
            onSelectCounter,
            onRemoveCounter,
            selectedGoals,
            onSelectGoals,
            onRemoveGoal,
            onChangeGoalBudget,
            weeklyLimit,
            onChangeWeeklyLimit,
            onChangeGoalCostType,
        } = usePromotionMetrics(company);

        const {
            selectedRegions,
            regionQuery,
            geoSuggests,
            onSelectRegion,
            onRemoveRegion,
            onInputRegion,
        } = useBusinessGeo(company?.regions);

        const { enableDateEdit, dateValue, onChangeDateValue, date, startDate, onChangeDate } =
            usePromotionDate(company);

        const { utmParams, onChangeUtm } = usePromotionUtm(company);

        const strategyProps = usePromotionStrategy(company, { enableWeeklyLimit, enableCrr });

        const { isSaving, onStartPromotion, onSavePromotion } = useSavePromotion(
            {
                date,
                weeklyLimit,
                selectedCounters,
                selectedGoals,
                disabledPosts,
                postsValue,
                selectedRegions,
                selectedLogin,
                utmParams,
                ...strategyProps,
            },
            company,
        );

        if (!contentPromotionEnabled) {
            navigate('/');
        }

        return (
            <DashboardPage pb={{ xs: 12, sm: 7 }}>
                <Typography
                    variant={isMobile ? 'title2' : 'title1'}
                    weight="bold"
                    mb={3}
                    mt={2}
                    px={2}
                >
                    {t(TITLE)}
                </Typography>
                <Flex direction={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', sm: 'auto' }}>
                    <Flex
                        width={{ xs: '100%', sm: DRAFT_MAX_WIDTH }}
                        height="100%"
                        borderRadius={2}
                    >
                        {isEditMode ? null : (
                            <ContentPromotionLogin
                                value={selectedLogin}
                                options={logins}
                                isLoaging={isLoagingLogins}
                                onChange={onChangeLogin}
                            />
                        )}
                        <ContentPromotionPosts
                            mt={isEditMode ? 0 : 2}
                            visiblePostsDialog={visiblePostsDialog}
                            value={postsValue}
                            posts={posts}
                            disabledPosts={disabledPosts}
                            onClose={onPostsDialogClose}
                            onSave={onPostsDialogSave}
                            onChange={onPostsFieldChange}
                        />
                        <ContentPromotionBudget
                            mt={2}
                            {...metrics}
                            selectedCounters={selectedCounters}
                            onSelectCounter={onSelectCounter}
                            onRemoveCounter={onRemoveCounter}
                            selectedGoals={selectedGoals}
                            onSelectGoals={onSelectGoals}
                            onRemoveGoal={onRemoveGoal}
                            onChangeGoalBudget={onChangeGoalBudget}
                            weeklyLimit={weeklyLimit}
                            onChangeWeeklyLimit={onChangeWeeklyLimit}
                            onChangeGoalCostType={onChangeGoalCostType}
                            {...strategyProps}
                        />
                        <GeoSuggestField
                            mt={2}
                            selected={selectedRegions}
                            suggests={geoSuggests}
                            value={regionQuery}
                            onSelect={onSelectRegion}
                            onRemove={onRemoveRegion}
                            onInput={onInputRegion}
                            required
                            withHint
                        />
                        {enableDateEdit ? (
                            <ContentPromotionDate
                                mt={2}
                                value={dateValue}
                                date={date}
                                startDate={startDate}
                                onChange={onChangeDateValue}
                                onChangeDate={onChangeDate}
                            />
                        ) : null}
                        <ContentPromotionUtm mt={2} value={utmParams} onChange={onChangeUtm} />
                    </Flex>
                    <Flex
                        width={{ xs: '100%', sm: STATS_MAX_WIDTH }}
                        ml={{ xs: 0, sm: 2 }}
                        borderRadius={{ xs: 'none', sm: 2 }}
                        background="var(--background-primary)"
                        position={{ xs: 'fixed', sm: 'static' }}
                        bottom={{ xs: 7, sm: 'auto' }}
                        borderTop={{ xs: '1px solid var(--divider)', sm: 'none' }}
                    >
                        {isMobile ? null : (
                            <ContentPromotionStats
                                login={selectedLogin}
                                postsValue={postsValue}
                                selectedCounters={selectedCounters}
                                selectedGoals={selectedGoals}
                                goals={metrics.goals}
                                regions={selectedRegions}
                                weeklyLimit={weeklyLimit}
                                date={date}
                                {...strategyProps}
                            />
                        )}
                        <Flex p={2} direction="row" width="100%">
                            <Button
                                variant="filled"
                                color="primary"
                                size="l"
                                width="100%"
                                disabled={isCampaignDisabled({
                                    selectedCounters,
                                    selectedRegions,
                                    selectedGoals,
                                    weeklyLimit,
                                    date,
                                    ...strategyProps,
                                    isSaving,
                                })}
                                onClick={isEditMode ? onSavePromotion : onStartPromotion}
                            >
                                {isEditMode ? t('Сохранить') : t('Запустить')}
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            </DashboardPage>
        );
    },
);
